import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Button, Box, Text } from '@chakra-ui/react';

import { useApi } from '@app/hooks';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';
import { ProfileContext } from '@app/contexts/profile';
import { MasterOutlet } from '@app/api/models/acb';
import { EditableGrid } from '@app/components/common/editable-grid/editable-grid';
import {
  GridDraft,
  GridView,
} from '@app/components/common/editable-grid/model';

import { loadRouteIds } from './collection-editor';
import { buildOutletUpdate, outletColumns, outletQuery } from './adapter';
import {
  channelOptions,
  outletGuideSources,
  searchOutletLookup,
} from './lookups';

export default function OutletsGrid() {
  const api = useApi();
  // useApi wrappers change on each render. Keep the current client without
  // treating its identity as a reason to reload guides or table rows.
  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  }, [api]);
  const history = useHistory();
  const { profile } = useContext(ProfileContext);
  const canEdit = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });
  const [guides, setGuides] = useState<Record<string, EntityBase[]>>({});
  const [guideError, setGuideError] = useState('');
  const [guideRevision, setGuideRevision] = useState(0);
  useEffect(() => {
    let live = true;
    Promise.all(
      Object.values(outletGuideSources).map((name) =>
        apiRef.current.guides.get({ name })
      )
    )
      .then((responses) => {
        if (!live) {
          return;
        }
        if (responses.some((r) => r?.code !== 200)) {
          throw new Error('Не удалось загрузить справочники');
        }
        const loaded = Object.fromEntries(
          Object.keys(outletGuideSources).map((id, i) => [
            id,
            responses[i].data || [],
          ])
        );
        loaded.channel = channelOptions(loaded.sub_channel);
        setGuides(loaded);
        setGuideError('');
      })
      .catch(() => {
        if (live) {
          setGuideError(
            'Не удалось загрузить справочники. Редактирование справочных полей недоступно.'
          );
        }
      });
    return () => {
      live = false;
    };
  }, [guideRevision]);
  const remoteLookups = useMemo(() => {
    const client = {
      get: (payload) => apiRef.current.autocomplete.get(payload),
      getPaginated: (payload) =>
        apiRef.current.autocomplete.getPaginated(payload),
    };
    return {
      distributor: searchOutletLookup(client, 'distributor'),
      route_numbers: searchOutletLookup(client, 'route_number'),
    };
  }, []);
  const columns = useMemo(
    () => outletColumns(guides, remoteLookups),
    [guides, remoteLookups]
  );
  const load = useCallback(async (view: GridView, page: number) => {
    const response = await apiRef.current.acb.getMasterOutlets(
      outletQuery(view, page)
    );
    if (response?.code !== 200 || !response.data) {
      throw new Error(
        response?.message || 'Не удалось загрузить торговые точки'
      );
    }
    return { data: response.data.data, total: response.data.total };
  }, []);
  const save = useCallback(
    async (draft: GridDraft<MasterOutlet>) => {
      if (!canEdit) {
        throw new Error('Недостаточно прав');
      }
      const response = await apiRef.current.acb.getMasterOutlet(
        draft.original.id
      );
      if (response?.code !== 200 || !response.data) {
        throw new Error(
          response?.message || 'Не удалось проверить актуальные данные'
        );
      }
      if ('route_numbers' in draft.changes)
        {response.data['route_number_id'] = await loadRouteIds(
          apiRef.current,
          draft.original.id
        );}
      const result = await apiRef.current.acb.modifyMasterOutletGrid(
        buildOutletUpdate(response.data, draft)
      );
      if (result?.code !== 200) {
        throw new Error(
          result?.message ||
            'Сохранение не подтверждено. Проверьте данные перед повтором.'
        );
      }
    },
    [canEdit]
  );
  // Do not share saved preferences between users or mount under an anonymous storage key.
  if (!profile?.id) {
    return <Text p={4}>Загрузка профиля…</Text>;
  }
  return (
    <Box>
      {guideError && (
        <Box mb={2}>
          <Text role="alert" color="red.600">
            {guideError}
          </Text>
          <Button size="sm" onClick={() => setGuideRevision((n) => n + 1)}>
            Повторить загрузку справочников
          </Button>
        </Box>
      )}
      <EditableGrid
        key={profile.id}
        title="Торговые точки"
        columns={columns}
        storageKey={`sneg:outlets-grid:v1:${profile.id}`}
        canEdit={canEdit}
        load={load}
        save={save}
        toolbar={
          <Button as={Link} to="/acl/outlets/" size="sm" variant="outline">
            Классическое представление
          </Button>
        }
        openForm={(row) =>
          history.push(`/acl/outlets/${row.id}/`, {
            masterOutlet: row,
            back: '/acl/outlets/grid/',
          })
        }
      />
    </Box>
  );
}
